import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Empty, Form, Input, InputNumber, Modal, Popconfirm, Skeleton, Switch, Table, Tabs, Tag } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  FiArrowLeft,
  FiBell,
  FiCreditCard,
  FiExternalLink,
  FiEye,
  FiFileText,
  FiMail,
  FiMessageCircle,
  FiPlus,
  FiSmartphone,
  FiTrendingUp,
  FiUpload,
  FiX,
} from 'react-icons/fi';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { HiOutlineUserGroup } from 'react-icons/hi2';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import PermissionGate from '../../components/auth/PermissionGate';
import BasicProfileEditDrawer from '../../components/profile/BasicProfileEditDrawer';
import BankMismatchReviewPanel from '../../components/bank/BankMismatchReviewPanel';
import BankManualUploadForm from '../../components/bank/BankManualUploadForm';
import BankRequestSubmittedModal from '../../components/bank/BankRequestSubmittedModal';
import ifaService from '../../services/ifaService';
import { baseUrl } from '../../api/baseUrls';
import { useAuth, useExplicitPermission } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import PartnerPerformanceReportTab from './_blocks/PartnerPerformanceReportTab';
import PartnerQuestionnaireTab from './_blocks/PartnerQuestionnaireTab';
import { formatIndianCount, formatRevenue } from '../product-master/pre-ipo/reporting/commons/preIpoReportingFormat.helpers';
import {
  DEMAT_FORMAT_HINT,
  DEMAT_MAX_LENGTH,
  formatDematInput,
  getDematInputError,
  validateDematNumber,
} from '../../utils/dematValidation';
import {
  applyProfileBankManualUpload,
  buildBankManualUploadState,
  getProfileAttemptedBankFields,
  resetProfileBankAddForm,
} from '../../utils/bankVerifyManualUpload';
import {
  resolveInvestorUuid,
  resolvePartnerUuid,
} from '../product-master/pre-ipo/order-management/orderEntityLinks';

const PLACE_SINGLE_SHARE_ORDER_PATH = '/pre-ipo/order-management/place';

const PRIMARY_COLOR = '#21B546';
const DEFAULT_PARTNER_MARGIN_CAP = 20;
const ADMIN_MAX_PARTNER_MARGIN_CAP = 100;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const DEMAT_CMR_MAX_SIZE = 2 * 1024 * 1024;
const DEMAT_CMR_ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const DEMAT_CMR_ALLOWED_EXTENSIONS = /\.(pdf|jpg|jpeg|png|webp)$/i;

const SELL_STAT_CONFIG = [
  { key: 'invested_investors_count', label: 'Invested Investors', color: 'text-green-600', type: 'count' },
  { key: 'total_transaction_value', label: 'Total Transaction Value', color: 'text-purple-600', type: 'revenue' },
  { key: 'transactions_completed', label: 'Transactions Completed', color: 'text-orange-500', type: 'count' },
  { key: 'partner_revenue', label: 'Partner Revenue', color: 'text-teal-600', type: 'revenue' },
  { key: 'altcase_revenue', label: 'Altcase Revenue', color: 'text-blue-600', type: 'revenue' },
];

const BUY_STAT_CONFIG = [
  { key: 'total_purchase_value', label: 'Total Purchase Value', color: 'text-purple-600', type: 'revenue' },
  { key: 'total_purchases', label: 'Total Purchases', color: 'text-blue-600', type: 'count' },
  { key: 'total_quantity_purchased', label: 'Total Quantity Purchased', color: 'text-green-600', type: 'count' },
  { key: 'altcase_revenue', label: 'Altcase Revenue', color: 'text-blue-600', type: 'revenue' },
];

const formatPortfolioStatValue = (value, type) => {
  if (value === null || value === undefined || value === '') return '—';
  if (type === 'revenue') return formatRevenue(value);
  return formatIndianCount(value);
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatTrendMonth = (entry) => {
  if (!entry) return '';
  const month = entry.month || '';
  const year = entry.year ? String(entry.year).slice(-2) : '';
  return year ? `${month} '${year}` : month;
};

const buildRegistrationTrendChartData = (trend = []) => {
  const trendByMonth = trend.reduce((acc, entry) => {
    if (entry?.month) {
      acc[entry.month] = {
        registered: Number(entry.registered) || 0,
        invested: Number(entry.invested) || 0,
      };
    }
    return acc;
  }, {});

  return MONTH_LABELS.map((month) => ({
    month,
    registered: trendByMonth[month]?.registered ?? 0,
    invested: trendByMonth[month]?.invested ?? 0,
  }));
};

const getProductTicker = (name) => {
  if (!name) return '—';
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 4);
  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .slice(0, 4)
    .toUpperCase();
};

const formatAUM = (value) => {
  if (value >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `${(value / 100000).toFixed(0)}L`;
  return value;
};

function ProductAvatar({ logoUrl, ticker, name }) {
  const [imageFailed, setImageFailed] = useState(false);
  const resolvedLogoUrl = logoUrl ? resolveFileUrl(logoUrl) : '';
  const showLogo = resolvedLogoUrl && !imageFailed;

  return (
    <div className="w-8 h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0 overflow-hidden">
      {showLogo ? (
        <img
          src={resolvedLogoUrl}
          alt={name || ticker || 'Product'}
          className="w-full h-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="text-[9px] font-bold text-gray-500 leading-none text-center">{ticker?.slice(0, 4)}</span>
      )}
    </div>
  );
}

const TAB_KEYS = {
  portfolio: 'portfolio',
  documents: 'documents',
  investors: 'investors',
  banks: 'banks',
  demats: 'demats',
  communication: 'communication',
  configuration: 'configuration',
  performanceReport: 'performanceReport',
  questionnaire: 'questionnaire',
};

const PARTNER_SECTION_TO_TAB = {
  banks: TAB_KEYS.banks,
};

function resolvePartnerTabFromParams(tab, section) {
  const tabKey = tab || PARTNER_SECTION_TO_TAB[section] || '';
  if (tabKey && Object.values(TAB_KEYS).includes(tabKey)) return tabKey;
  return TAB_KEYS.portfolio;
}

const COMMUNICATION_FIELDS = [
  {
    key: 'email_communication_partner',
    title: 'Receive Email Notifications (IFA)',
    subtitle: 'Get payment confirmations in your email',
    footer: 'Personal notifications',
    Icon: FiMail,
    cardClass: 'bg-gradient-to-br from-[#f8fcf6] to-[#edf8e8] border-[#d6ebcc]',
    titleClass: 'text-[#4a9a38]',
    footerClass: 'text-[#6bb657]',
    iconClass: 'text-[#65b84f] bg-white border-[#dfeeda]',
  },
  {
    key: 'email_communication_investor',
    title: 'Email Notifications to Investor',
    subtitle: 'Send email notifications directly to investors',
    footer: 'Investor notifications',
    Icon: FiBell,
    cardClass: 'bg-gradient-to-br from-[#fdf9ff] to-[#f2e9fb] border-[#eadcf8]',
    titleClass: 'text-[#6c37b7]',
    footerClass: 'text-[#8f5dd1]',
    iconClass: 'text-[#7a42c6] bg-white border-[#ebdef9]',
  },
  {
    key: 'whatsapp_communication_investor',
    title: 'WhatsApp Notifications to Investor',
    subtitle: 'Send WhatsApp notifications directly to investors',
    footer: 'Instant messaging',
    Icon: FiMessageCircle,
    cardClass: 'bg-gradient-to-br from-[#f7fcff] to-[#e8f5fb] border-[#d4edf7]',
    titleClass: 'text-[#2d6dc0]',
    footerClass: 'text-[#4b9fd3]',
    iconClass: 'text-[#4aa8dc] bg-white border-[#d8eef7]',
  },
  {
    key: 'news_email_notifications_partner',
    title: 'News Email Notifications',
    subtitle: 'Receive news and updates via email',
    footer: 'News updates',
    Icon: FiMail,
    cardClass: 'bg-gradient-to-br from-[#fffdf8] to-[#fbf2e2] border-[#f2e4c8]',
    titleClass: 'text-[#bd7b1f]',
    footerClass: 'text-[#d39b47]',
    iconClass: 'text-[#d09a43] bg-white border-[#f4e8cf]',
  },
  {
    key: 'news_whatsapp_notifications_partner',
    title: 'News WhatsApp Notifications',
    subtitle: 'Receive news and updates via WhatsApp',
    footer: 'News updates',
    Icon: FiSmartphone,
    cardClass: 'bg-gradient-to-br from-[#f7fefe] to-[#e5f6f5] border-[#cdebea]',
    titleClass: 'text-[#2ea0a1]',
    footerClass: 'text-[#63c5c4]',
    iconClass: 'text-[#5ec3c1] bg-white border-[#d8efee]',
  },
];

const safeValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return value;
};

const formatDate = (value) => (value ? dayjs(value).format('DD MMM YYYY') : '—');

const resolveFileUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

const isImageUrl = (url) => /\.(jpg|jpeg|png|gif|webp)$/i.test(url || '');

function getKycDocumentId(doc) {
  return doc?.uuid || doc?.doc_id || doc?.document_id || '';
}

function getUploadedDocTagColor(status) {
  const normalized = String(status || 'UPLOADED').toUpperCase();
  if (normalized === 'VERIFIED') return 'green';
  if (normalized === 'REJECTED') return 'red';
  return 'orange';
}

function splitPartnerDocuments(uploaded, pending) {
  const uploadedList = Array.isArray(uploaded) ? uploaded : [];
  const pendingList = Array.isArray(pending) ? pending : [];
  const groups = new Map();

  uploadedList.forEach((doc) => {
    const type = String(doc.document_type || '');
    if (!type) return;
    const group = groups.get(type) || [];
    group.push(doc);
    groups.set(type, group);
  });

  const visibleUploaded = [];
  const rejectedPending = [];

  groups.forEach((group, type) => {
    const rejectedUrls = new Set(
      group
        .filter((doc) => String(doc.status || '').toUpperCase() === 'REJECTED')
        .map((doc) => String(doc.document_url || ''))
        .filter(Boolean)
    );
    const nonRejected = group.filter(
      (doc) => String(doc.status || '').toUpperCase() !== 'REJECTED'
    );
    if (nonRejected.length) {
      const replacements = nonRejected.filter(
        (doc) => !rejectedUrls.has(String(doc.document_url || ''))
      );
      const pool = replacements.length ? replacements : nonRejected;
      visibleUploaded.push(pool[pool.length - 1]);
      return;
    }
    const latestRejected = group[group.length - 1];
    rejectedPending.push({
      key: `rejected-${type}`,
      document_type: type,
      label: type.replace(/_/g, ' '),
      sub_label: 'Rejected — re-upload a new file',
      document_url: latestRejected.document_url,
      status: 'REJECTED',
      is_rejected: true,
    });
  });

  const untyped = uploadedList.filter((doc) => {
    if (String(doc.document_type || '')) return false;
    return String(doc.status || '').toUpperCase() !== 'REJECTED';
  });
  const pendingTypes = new Set(
    pendingList.map((doc) => String(doc.document_type || '')).filter(Boolean)
  );

  return {
    uploaded: [...visibleUploaded, ...untyped],
    pending: [
      ...pendingList,
      ...rejectedPending.filter((doc) => !pendingTypes.has(doc.document_type)),
    ],
  };
}

const isPdfUrl = (url) => /\.pdf$/i.test(url || '');

const formatDematStatus = (status) => {
  if (!status) return 'Unknown';
  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getDematStatusTagColor = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'verified' || normalized === 'active') return 'green';
  if (normalized === 'under_review' || normalized === 'pending') return 'orange';
  if (normalized === 'rejected' || normalized === 'failed') return 'red';
  return 'default';
};

function DematAccountCard({ demat, onEdit }) {
  const dematFileUrl = demat.demat_url || demat.cmr_document_url ? resolveFileUrl(demat.demat_url || demat.cmr_document_url) : '';
  const hasDocument = Boolean(dematFileUrl);
  const isImage = isImageUrl(dematFileUrl);
  const isPdf = isPdfUrl(dematFileUrl);
  const dematStatus = demat.status || demat.demat_status;

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      {/* ── Header row ── */}
      <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
          <FiCreditCard className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{safeValue(demat.dp_name)}</p>
          <p className="text-xs text-gray-400">Depository Participant</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {onEdit ? (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              className="px-1"
              onClick={() => onEdit(demat)}
            >
              Edit
            </Button>
          ) : null}
          {demat.is_primary ? (
            <Tag color="green" className="m-0 font-medium">Primary</Tag>
          ) : null}
          <Tag color={getDematStatusTagColor(dematStatus)} className="m-0 font-medium">
            {formatDematStatus(dematStatus)}
          </Tag>
        </div>
      </div>

      {/* ── Demat number ── */}
      <div className="px-5 py-4">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">Demat Number</p>
        <p className="font-mono text-sm font-bold text-gray-900 tracking-wide">{safeValue(demat.demat_account_no || demat.demat_number)}</p>
      </div>

      {/* ── Document preview (only if URL exists) ── */}
      {hasDocument ? (
        <div className="border-t border-gray-100 bg-gray-50/60 px-5 py-4">
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-gray-400">CMR / CML Document</p>
          <div className="flex items-center gap-4">
            {/* thumbnail / icon */}
            {isImage ? (
              <a
                href={dematFileUrl}
                target="_blank"
                rel="noreferrer"
                className="group relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-gray-100"
              >
                <img
                  src={dematFileUrl}
                  alt="CMR document"
                  className="h-full w-full object-cover transition group-hover:opacity-80"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 transition group-hover:opacity-100">
                  <FiEye className="h-5 w-5 text-white drop-shadow" />
                </div>
              </a>
            ) : (
              <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center gap-1 rounded-xl border border-red-100 bg-red-50">
                <FiFileText className="h-7 w-7 text-red-400" />
                <span className="text-[10px] font-semibold uppercase text-red-400">{isPdf ? 'PDF' : 'File'}</span>
              </div>
            )}

            {/* meta + action */}
            <div className="min-w-0 flex-1">
              <p className="mb-0.5 text-sm font-medium text-gray-800">
                {isImage ? 'Image Document' : isPdf ? 'PDF Document' : 'Uploaded File'}
              </p>
              <p className="mb-3 text-xs text-gray-400">
                {isImage ? 'Click to preview in a new tab' : 'Click to open in a new tab'}
              </p>
              <a
                href={dematFileUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-600 transition hover:bg-blue-100 hover:border-blue-300"
              >
                <FiExternalLink className="h-3.5 w-3.5" />
                View Document
              </a>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function PartnerDetailsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canEditPartner = hasPermission(PERMISSIONS.PARTNER_EDIT);
  const canReviewKycDocuments = isSuperAdmin || hasPermission(PERMISSIONS.RBAC_USER_MANAGEMENT_EDIT);
  const canManageBankDematAccounts = hasPermission(PERMISSIONS.PARTNER_EDIT);
  const canSetPrimaryBank = hasPermission(PERMISSIONS.BANK_SET_PRIMARY);
  const canEditDematNumber = hasPermission(PERMISSIONS.DEMAT_NUMBER_EDIT);
  const canViewPartnerMargin = useExplicitPermission(PERMISSIONS.PARTNER_MARGIN_EDIT);
  const canEditPartnerMargin = canViewPartnerMargin;
  const { partner_id: partnerId } = useParams();
  const normalizedPartnerId = partnerId ? String(partnerId).trim() : '';
  const tabFromUrl = searchParams.get('tab')?.trim() || '';
  const sectionFromUrl = searchParams.get('section')?.trim() || '';
  const resolvedTabFromUrl = useMemo(
    () => resolvePartnerTabFromParams(tabFromUrl, sectionFromUrl),
    [tabFromUrl, sectionFromUrl]
  );
  const [activeTab, setActiveTab] = useState(resolvedTabFromUrl);
  const [loadedTabs, setLoadedTabs] = useState({});
  const [tabLoading, setTabLoading] = useState({});
  const [tabError, setTabError] = useState({});
  const [tabData, setTabData] = useState({});
  const [investorsFetching, setInvestorsFetching] = useState(false);
  const [bankForm] = Form.useForm();
  const [dematForm] = Form.useForm();
  const [dematEditForm] = Form.useForm();
  const tabDataRef = useRef(tabData);

  useEffect(() => {
    tabDataRef.current = tabData;
  }, [tabData]);

  const [uploadingDocType, setUploadingDocType] = useState('');
  const [reviewingDocId, setReviewingDocId] = useState('');
  const [rejectDoc, setRejectDoc] = useState(null);
  const [approveDoc, setApproveDoc] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [communicationDraft, setCommunicationDraft] = useState({});
  const [isCommunicationEditing, setIsCommunicationEditing] = useState(false);
  const [savingCommunication, setSavingCommunication] = useState(false);
  const [isBankFormOpen, setIsBankFormOpen] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [bankIfscLookupLoading, setBankIfscLookupLoading] = useState(false);
  const [bankIfscDetails, setBankIfscDetails] = useState(null);
  const [bankIfscError, setBankIfscError] = useState('');
  const [bankMismatchReview, setBankMismatchReview] = useState(null);
  const [bankMismatchLoading, setBankMismatchLoading] = useState(false);
  const [bankMismatchError, setBankMismatchError] = useState('');
  const [bankMismatchActionLoading, setBankMismatchActionLoading] = useState(false);
  const [bankManualUploadData, setBankManualUploadData] = useState(null);
  const [bankSubmitSuccessOpen, setBankSubmitSuccessOpen] = useState(false);
  const [bankSubmitSuccessMessage, setBankSubmitSuccessMessage] = useState('');
  const [settingPrimaryBankUuid, setSettingPrimaryBankUuid] = useState('');
  const [deletingBankUuid, setDeletingBankUuid] = useState('');
  const [isDematFormOpen, setIsDematFormOpen] = useState(false);
  const [savingDemat, setSavingDemat] = useState(false);
  const [dematCmrFile, setDematCmrFile] = useState(null);
  const [editingDemat, setEditingDemat] = useState(null);
  const [savingDematEdit, setSavingDematEdit] = useState(false);
  const [savingTransferBeforePayment, setSavingTransferBeforePayment] = useState(false);
  const [marginCapDraft, setMarginCapDraft] = useState({
    is_margin_cap_override_enabled: false,
    max_margin_cap_percentage: null,
  });
  const [savingMarginCap, setSavingMarginCap] = useState(false);
  const [basicProfileDrawerOpen, setBasicProfileDrawerOpen] = useState(false);
  const [savingBasicProfile, setSavingBasicProfile] = useState(false);

  const resolveSectionPayload = useCallback((data, section) => {
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== 'object') return {};
    const nested = data[section];
    if (Array.isArray(nested)) return nested;
    if (nested && typeof nested === 'object') return nested;
    // New API shape returns section payload directly under data
    return data;
  }, []);

  const loadBasic = useCallback(async () => {
    if (!normalizedPartnerId) return;
    try {
      const data = await ifaService.getPartnerProfileSection(normalizedPartnerId, 'basic');
      setTabData((prev) => ({ ...prev, basic: resolveSectionPayload(data, 'basic') }));
      setTabError((prev) => ({ ...prev, basic: '' }));
    } catch (error) {
      setTabError((prev) => ({
        ...prev,
        basic: error?.response?.data?.message || 'Failed to load partner details.',
      }));
    }
  }, [normalizedPartnerId, resolveSectionPayload]);

  const sectionByTab = useMemo(
    () => ({
      [TAB_KEYS.portfolio]: 'portfolio',
      [TAB_KEYS.documents]: 'documents',
      [TAB_KEYS.investors]: 'investors',
      [TAB_KEYS.banks]: 'banks',
      [TAB_KEYS.demats]: 'demats',
      [TAB_KEYS.communication]: 'communication',
      [TAB_KEYS.questionnaire]: 'questionnaire',
    }),
    []
  );

  const loadInvestors = useCallback(
    async (page = 1, limit = 10, { useTabLoading = false } = {}) => {
      if (!normalizedPartnerId) return;

      if (useTabLoading) {
        setTabLoading((prev) => ({ ...prev, [TAB_KEYS.investors]: true }));
      } else {
        setInvestorsFetching(true);
      }
      setTabError((prev) => ({ ...prev, [TAB_KEYS.investors]: '' }));
      try {
        const data = await ifaService.getPartnerProfileSection(normalizedPartnerId, 'investors', {
          page,
          limit,
        });
        const sectionPayload = resolveSectionPayload(data, 'investors');
        setTabData((prev) => ({ ...prev, [TAB_KEYS.investors]: sectionPayload }));
        setLoadedTabs((prev) => ({ ...prev, [TAB_KEYS.investors]: true }));
      } catch (error) {
        setTabError((prev) => ({
          ...prev,
          [TAB_KEYS.investors]: error?.response?.data?.message || 'Failed to load section data.',
        }));
      } finally {
        if (useTabLoading) {
          setTabLoading((prev) => ({ ...prev, [TAB_KEYS.investors]: false }));
        } else {
          setInvestorsFetching(false);
        }
      }
    },
    [normalizedPartnerId, resolveSectionPayload]
  );

  const ensureTabData = useCallback(
    async (tabKey, force = false) => {
      if (
        !normalizedPartnerId
        || tabKey === TAB_KEYS.performanceReport
        || tabKey === TAB_KEYS.configuration
      ) return;
      if (!force && loadedTabs[tabKey]) return;
      const section = sectionByTab[tabKey];
      if (!section) return;

      if (section === 'investors') {
        const investorsPayload = tabDataRef.current[TAB_KEYS.investors];
        const page = force ? (investorsPayload?.pagination?.page || 1) : 1;
        const limit = force ? (investorsPayload?.pagination?.limit || 10) : 10;
        await loadInvestors(page, limit, { useTabLoading: true });
        return;
      }

      setTabLoading((prev) => ({ ...prev, [tabKey]: true }));
      setTabError((prev) => ({ ...prev, [tabKey]: '' }));
      try {
        const data = await ifaService.getPartnerProfileSection(normalizedPartnerId, section);
        const sectionPayload = resolveSectionPayload(data, section);
        setTabData((prev) => ({ ...prev, [tabKey]: sectionPayload }));
        setLoadedTabs((prev) => ({ ...prev, [tabKey]: true }));
        if (section === 'communication') {
          setCommunicationDraft(resolveSectionPayload(data, 'communication'));
        }
      } catch (error) {
        setTabError((prev) => ({
          ...prev,
          [tabKey]: error?.response?.data?.message || 'Failed to load section data.',
        }));
      } finally {
        setTabLoading((prev) => ({ ...prev, [tabKey]: false }));
      }
    },
    [loadedTabs, loadInvestors, normalizedPartnerId, resolveSectionPayload, sectionByTab]
  );

  const loadBankMismatchReview = useCallback(async () => {
    if (!normalizedPartnerId) return;
    setBankMismatchLoading(true);
    setBankMismatchError('');
    try {
      const data = await ifaService.getBankMismatchPendingReview(normalizedPartnerId);
      setBankMismatchReview(data);
    } catch (error) {
      setBankMismatchReview(null);
      setBankMismatchError(error?.response?.data?.message || 'Failed to load pending bank mismatch review.');
    } finally {
      setBankMismatchLoading(false);
    }
  }, [normalizedPartnerId]);

  useEffect(() => {
    if (activeTab !== TAB_KEYS.banks || !loadedTabs[TAB_KEYS.banks]) return;
    loadBankMismatchReview();
  }, [activeTab, loadedTabs, loadBankMismatchReview]);

  useEffect(() => {
    if (!normalizedPartnerId) return;
    setActiveTab(resolvedTabFromUrl);
    ensureTabData(resolvedTabFromUrl);
  }, [normalizedPartnerId, resolvedTabFromUrl, ensureTabData]);

  useEffect(() => {
    if (!normalizedPartnerId) return;
    const hasSectionOnly = sectionFromUrl && !tabFromUrl;
    const hasInvalidTab = tabFromUrl && tabFromUrl !== resolvedTabFromUrl;
    if (!hasSectionOnly && !hasInvalidTab) return;
    const params = new URLSearchParams();
    if (resolvedTabFromUrl !== TAB_KEYS.portfolio) params.set('tab', resolvedTabFromUrl);
    setSearchParams(params, { replace: true });
  }, [normalizedPartnerId, tabFromUrl, sectionFromUrl, resolvedTabFromUrl, setSearchParams]);

  const refreshAfterBasicProfileEdit = useCallback(async () => {
    await loadBasic();

    if (loadedTabs[activeTab]) {
      await ensureTabData(activeTab, true);
    }
  }, [activeTab, ensureTabData, loadBasic, loadedTabs]);

  useEffect(() => {
    loadBasic();
  }, [loadBasic]);

  useEffect(() => {
    if (!tabData.basic || typeof tabData.basic !== 'object') return;
    setMarginCapDraft({
      is_margin_cap_override_enabled: Boolean(tabData.basic.is_margin_cap_override_enabled),
      max_margin_cap_percentage:
        tabData.basic.max_margin_cap_percentage != null
          ? Number(tabData.basic.max_margin_cap_percentage)
          : null,
    });
  }, [tabData.basic]);

  useEffect(() => {
    ensureTabData(TAB_KEYS.portfolio);
    ensureTabData(TAB_KEYS.documents);
  }, [ensureTabData]);

  const handleUploadDocument = async (pendingDoc, file) => {
    if (!file || !pendingDoc?.document_type || !normalizedPartnerId) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be under 10 MB.');
      return;
    }
    setUploadingDocType(pendingDoc.document_type);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', pendingDoc.document_type);
      await ifaService.uploadDocument(normalizedPartnerId, formData);
      toast.success(`${pendingDoc.label || pendingDoc.document_type} uploaded successfully.`);
      await ensureTabData(TAB_KEYS.documents, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to upload document.');
    } finally {
      setUploadingDocType('');
    }
  };

  const handleReviewDocument = async (doc, status, reason) => {
    const documentId = getKycDocumentId(doc);
    if (!normalizedPartnerId || !documentId) {
      toast.error('Document ID is missing.');
      return;
    }

    setReviewingDocId(String(documentId));
    try {
      const payload = { status };
      if (status === 'REJECTED' && reason) payload.rejection_reason = reason;
      const response = await ifaService.reviewPartnerDocument(normalizedPartnerId, documentId, payload);
      if (response?.success === false) {
        toast.error(response?.message || 'Failed to review document.');
        return;
      }
      toast.success(
        response?.message
          || (status === 'VERIFIED' ? 'Document verified successfully.' : 'Document rejected successfully.')
      );
      setRejectDoc(null);
      setApproveDoc(null);
      setRejectionReason('');
      await ensureTabData(TAB_KEYS.documents, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to review document.');
    } finally {
      setReviewingDocId('');
    }
  };

  const handleAddBankAccount = async (values) => {
    if (!normalizedPartnerId || !canManageBankDematAccounts) return;
    const payload = {
      account_number: String(values.account_number || '').trim(),
      ifsc_code: String(values.ifsc_code || '').trim().toUpperCase(),
    };

    setSavingBank(true);
    try {
      const response = await ifaService.addPartnerBankAccount(normalizedPartnerId, payload);
      if (response?.success === false) {
        const attempted = getProfileAttemptedBankFields(bankForm, bankIfscDetails);
        const manualUpload = buildBankManualUploadState(response, attempted);
        applyProfileBankManualUpload(manualUpload, {
          bankForm,
          setBankIfscDetails,
          setManualUploadData: setBankManualUploadData,
          setBankIfscError,
        });
        return;
      }
      toast.success(response?.message || 'Bank account added successfully.');
      resetProfileBankAddForm({
        bankForm,
        setBankIfscDetails,
        setBankIfscError,
        setManualUploadData: setBankManualUploadData,
        setIsBankFormOpen,
      });
      await ensureTabData(TAB_KEYS.banks, true);
      await loadBankMismatchReview();
    } catch (error) {
      const attempted = getProfileAttemptedBankFields(bankForm, bankIfscDetails);
      const manualUpload = buildBankManualUploadState(error, attempted);
      applyProfileBankManualUpload(manualUpload, {
        bankForm,
        setBankIfscDetails,
        setManualUploadData: setBankManualUploadData,
        setBankIfscError,
      });
    } finally {
      setSavingBank(false);
    }
  };

  const handleManualUploadSubmit = async ({
    accountNumber,
    ifscCode,
    bankName,
    branchName,
    chequeFile,
    otherDocFile,
  }) => {
    if (!normalizedPartnerId || !canManageBankDematAccounts) return;
    setSavingBank(true);
    try {
      const fd = new FormData();
      fd.append('account_number', accountNumber);
      fd.append('ifsc_code', ifscCode);
      fd.append('bank_name', bankName);
      fd.append('branch_name', branchName || '');
      fd.append('cheque', chequeFile);
      if (otherDocFile) fd.append('supporting_document', otherDocFile);
      const response = await ifaService.uploadBankMismatchCheque(normalizedPartnerId, fd);
      resetProfileBankAddForm({
        bankForm,
        setBankIfscDetails,
        setBankIfscError,
        setManualUploadData: setBankManualUploadData,
        setIsBankFormOpen,
      });
      setBankSubmitSuccessMessage(response?.message || '');
      setBankSubmitSuccessOpen(true);
      await ensureTabData(TAB_KEYS.banks, true);
      await loadBankMismatchReview();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to upload documents.');
    } finally {
      setSavingBank(false);
    }
  };

  const handleManualUploadCancel = () => {
    setBankManualUploadData(null);
    bankForm.resetFields();
    setBankIfscDetails(null);
    setBankIfscError('');
  };

  const handleSetPrimaryBank = async (bankUuid) => {
    if (!normalizedPartnerId || !canSetPrimaryBank || !bankUuid) return;
    setSettingPrimaryBankUuid(bankUuid);
    try {
      const response = await ifaService.setPrimaryBankAccount(normalizedPartnerId, bankUuid);
      toast.success(response?.message || 'Primary bank account updated successfully.');
      await ensureTabData(TAB_KEYS.banks, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to set primary bank account.');
    } finally {
      setSettingPrimaryBankUuid('');
    }
  };

  const handleDeleteBankAccount = async (bankUuid) => {
    if (!normalizedPartnerId || !canManageBankDematAccounts || !bankUuid) return;
    setDeletingBankUuid(bankUuid);
    try {
      const response = await ifaService.deletePartnerBankAccount(normalizedPartnerId, bankUuid);
      toast.success(response?.message || 'Bank account removed successfully.');
      await ensureTabData(TAB_KEYS.banks, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to remove bank account.');
    } finally {
      setDeletingBankUuid('');
    }
  };

  const handleBankMismatchVerify = async (status) => {
    if (!normalizedPartnerId || !canManageBankDematAccounts) return;
    const documentId = bankMismatchReview?.pending_review?.cheque_document?.document_id;
    if (!documentId) return;

    setBankMismatchActionLoading(true);
    try {
      const response = await ifaService.verifyBankMismatchDocument(
        normalizedPartnerId,
        documentId,
        status
      );
      toast.success(
        response?.message
          || (status === 'VERIFIED' ? 'Document verified successfully.' : 'Document rejected successfully.')
      );
      await ensureTabData(TAB_KEYS.banks, true);
      await loadBankMismatchReview();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update bank mismatch review.');
    } finally {
      setBankMismatchActionLoading(false);
    }
  };

  const handleBankIfscChange = async (value) => {
    const nextIfsc = String(value || '').trim().toUpperCase();
    setBankIfscDetails(null);
    setBankIfscError('');
    bankForm.setFieldValue('ifsc_code', nextIfsc);

    if (!IFSC_REGEX.test(nextIfsc)) {
      setBankIfscLookupLoading(false);
      return;
    }

    setBankIfscLookupLoading(true);
    try {
      const response = await ifaService.lookupIFSC(nextIfsc);
      if (bankForm.getFieldValue('ifsc_code') !== nextIfsc) return;
      const details = response?.data || {};
      setBankIfscDetails({
        bank_name: details.bank_name || '',
        branch_name: details.branch_name || '',
        address: details.address || '',
      });
    } catch (error) {
      if (bankForm.getFieldValue('ifsc_code') !== nextIfsc) return;
      setBankIfscDetails(null);
      setBankIfscError(error?.response?.data?.message || 'Could not fetch bank details for this IFSC.');
    } finally {
      if (bankForm.getFieldValue('ifsc_code') === nextIfsc) {
        setBankIfscLookupLoading(false);
      }
    }
  };

  const handleAddDematAccount = async (values) => {
    if (!normalizedPartnerId || !canManageBankDematAccounts) return;
    if (!dematCmrFile) {
      toast.error('Please upload a CMR document.');
      return;
    }
    if (dematCmrFile.size > DEMAT_CMR_MAX_SIZE) {
      toast.error('CMR document size must be under 2 MB.');
      return;
    }
    if (
      !DEMAT_CMR_ALLOWED_TYPES.includes(dematCmrFile.type)
      && !DEMAT_CMR_ALLOWED_EXTENSIONS.test(dematCmrFile.name || '')
    ) {
      toast.error('Only PDF, JPG, JPEG, PNG, or WebP files are allowed.');
      return;
    }

    const dematCheck = validateDematNumber(values.demat_number);
    if (!dematCheck.valid) {
      toast.error(dematCheck.message || 'Enter a valid demat number.');
      return;
    }

    const dematNumber = String(values.demat_number || '').trim().toUpperCase();
    const formData = new FormData();
    formData.append('demat_number', dematNumber);
    formData.append('file', dematCmrFile, dematCmrFile.name || 'cmr-document');

    setSavingDemat(true);
    try {
      const response = await ifaService.uploadPartnerDematCmr(normalizedPartnerId, formData);
      toast.success(response?.message || 'Demat account added successfully.');
      dematForm.resetFields();
      setDematCmrFile(null);
      setIsDematFormOpen(false);
      await ensureTabData(TAB_KEYS.demats, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add demat account.');
    } finally {
      setSavingDemat(false);
    }
  };

  const handleDematCmrFileChange = (file) => {
    if (!file) return;
    if (file.size > DEMAT_CMR_MAX_SIZE) {
      toast.error('CMR document size must be under 2 MB.');
      return;
    }
    if (!DEMAT_CMR_ALLOWED_TYPES.includes(file.type) && !DEMAT_CMR_ALLOWED_EXTENSIONS.test(file.name || '')) {
      toast.error('Only PDF, JPG, JPEG, PNG, or WebP files are allowed.');
      return;
    }
    setDematCmrFile(file);
  };

  const handleOpenDematEdit = (demat) => {
    const dematUuid = demat?.uuid;
    if (!dematUuid) {
      toast.error('Demat account identifier is missing.');
      return;
    }
    const currentNumber = String(demat.demat_number || demat.demat_account_no || '').trim();
    setEditingDemat({ uuid: dematUuid, demat_number: currentNumber });
    dematEditForm.setFieldsValue({ demat_number: currentNumber });
  };

  const handleCloseDematEdit = () => {
    setEditingDemat(null);
    dematEditForm.resetFields();
  };

  const handleEditDematNumber = async (values) => {
    if (!normalizedPartnerId || !canEditDematNumber || !editingDemat?.uuid) return;

    const dematCheck = validateDematNumber(values.demat_number);
    if (!dematCheck.valid) {
      toast.error(dematCheck.message || 'Enter a valid demat number.');
      return;
    }

    const dematNumber = String(values.demat_number || '').trim().toUpperCase();
    setSavingDematEdit(true);
    try {
      const response = await ifaService.editDematAccount(normalizedPartnerId, editingDemat.uuid, {
        demat_number: dematNumber,
      });
      toast.success(response?.message || 'Demat number updated successfully.');
      handleCloseDematEdit();
      await ensureTabData(TAB_KEYS.demats, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update demat number.');
    } finally {
      setSavingDematEdit(false);
    }
  };

  const handleSaveCommunication = async () => {
    if (!normalizedPartnerId) return;
    setSavingCommunication(true);
    try {
      await ifaService.savePartnerCommunication(normalizedPartnerId, communicationDraft);
      toast.success('Communication settings saved.');
      setIsCommunicationEditing(false);
      await ensureTabData(TAB_KEYS.communication, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save communication settings.');
    } finally {
      setSavingCommunication(false);
    }
  };

  const handleMarginOverrideChange = async (checked) => {
    if (!normalizedPartnerId || !canEditPartnerMargin) return;

    if (!checked) {
      setSavingMarginCap(true);
      try {
        const updated = await ifaService.updatePartnerMarginCapSettings(normalizedPartnerId, {
          is_margin_cap_override_enabled: false,
        });
        setMarginCapDraft({
          is_margin_cap_override_enabled: false,
          max_margin_cap_percentage: null,
        });
        setTabData((prev) => ({
          ...prev,
          basic: {
            ...prev.basic,
            is_margin_cap_override_enabled: false,
            max_margin_cap_percentage: null,
            effective_margin_cap_percentage:
              updated.effective_margin_cap_percentage ?? DEFAULT_PARTNER_MARGIN_CAP,
          },
        }));
        toast.success('Partner margin cap reset to default 20%.');
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to update partner margin cap.');
      } finally {
        setSavingMarginCap(false);
      }
      return;
    }

    setMarginCapDraft((prev) => ({
      is_margin_cap_override_enabled: true,
      max_margin_cap_percentage:
        prev.max_margin_cap_percentage != null ? prev.max_margin_cap_percentage : 25,
    }));
  };

  const handleSaveMarginCap = async () => {
    if (!normalizedPartnerId || !canEditPartnerMargin) return;

    const cap = Number(marginCapDraft.max_margin_cap_percentage);
    if (!Number.isFinite(cap) || cap <= DEFAULT_PARTNER_MARGIN_CAP) {
      toast.error(`Max margin must be greater than ${DEFAULT_PARTNER_MARGIN_CAP}%.`);
      return;
    }
    if (cap > ADMIN_MAX_PARTNER_MARGIN_CAP) {
      toast.error(`Max margin cannot exceed ${ADMIN_MAX_PARTNER_MARGIN_CAP}%.`);
      return;
    }

    setSavingMarginCap(true);
    try {
      const updated = await ifaService.updatePartnerMarginCapSettings(normalizedPartnerId, {
        is_margin_cap_override_enabled: true,
        max_margin_cap_percentage: cap,
      });
      setTabData((prev) => ({
        ...prev,
        basic: {
          ...prev.basic,
          is_margin_cap_override_enabled: true,
          max_margin_cap_percentage: updated.max_margin_cap_percentage ?? cap,
          effective_margin_cap_percentage:
            updated.effective_margin_cap_percentage ?? cap,
        },
      }));
      setMarginCapDraft({
        is_margin_cap_override_enabled: true,
        max_margin_cap_percentage: updated.max_margin_cap_percentage ?? cap,
      });
      toast.success(`Partner margin cap updated to ${updated.effective_margin_cap_percentage ?? cap}%.`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save partner margin cap.');
    } finally {
      setSavingMarginCap(false);
    }
  };

  const handleTransferBeforePaymentChange = async (checked) => {
    if (!normalizedPartnerId || !canEditPartner) return;
    setSavingTransferBeforePayment(true);
    try {
      await ifaService.updatePartnerOrderCreditSettings(normalizedPartnerId, {
        allow_transfer_before_payment: checked,
      });
      setTabData((prev) => ({
        ...prev,
        basic: { ...prev.basic, allow_transfer_before_payment: checked },
      }));
      toast.success(
        checked
          ? 'Trusted partner: share transfer before payment enabled.'
          : 'Trusted partner: share transfer before payment disabled.',
      );
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update trusted partner setting.');
    } finally {
      setSavingTransferBeforePayment(false);
    }
  };

  const handleBasicProfileSubmit = async (payload) => {
    if (!normalizedPartnerId) return;
    setSavingBasicProfile(true);
    try {
      const response = await ifaService.updatePartnerBasicProfile(normalizedPartnerId, payload);
      await refreshAfterBasicProfileEdit();
      toast.success(response?.message || 'Basic profile updated successfully.');
      setBasicProfileDrawerOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update basic profile.');
      throw error;
    } finally {
      setSavingBasicProfile(false);
    }
  };

  const renderTabBody = (tabKey, content) => {
    if (tabLoading[tabKey]) {
      return (
        <div className="rounded-2xl border border-gray-100 bg-white p-6">
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      );
    }
    if (tabError[tabKey]) {
      return (
        <Alert
          type="error"
          showIcon
          message={tabError[tabKey]}
          action={
            <Button size="small" onClick={() => ensureTabData(tabKey, true)}>
              Retry
            </Button>
          }
        />
      );
    }
    return content;
  };

  const basic = tabData.basic || {};
  const effectiveMarginCap =
    basic.effective_margin_cap_percentage != null
      ? Number(basic.effective_margin_cap_percentage)
      : DEFAULT_PARTNER_MARGIN_CAP;
  const hasMarginCapChanges = useMemo(() => {
    const basicEnabled = Boolean(basic.is_margin_cap_override_enabled);
    const draftEnabled = Boolean(marginCapDraft.is_margin_cap_override_enabled);
    if (basicEnabled !== draftEnabled) return draftEnabled;
    if (!draftEnabled) return false;
    return Number(marginCapDraft.max_margin_cap_percentage) !== Number(basic.max_margin_cap_percentage);
  }, [basic.is_margin_cap_override_enabled, basic.max_margin_cap_percentage, marginCapDraft]);
  const portfolioData = tabData[TAB_KEYS.portfolio] || {};
  const sellStats = portfolioData.sell_stats || {};
  const buyStats = portfolioData.buy_stats || {};
  const investorRegistrationSummary = portfolioData.investor_registration_summary || {};
  const investmentTrendData = useMemo(
    () =>
      (portfolioData.investment_trend || []).map((entry) => ({
        month: formatTrendMonth(entry),
        aum: Number(entry.aum) || 0,
      })),
    [portfolioData.investment_trend]
  );
  const investorRegistrationTrendData = useMemo(
    () => buildRegistrationTrendChartData(portfolioData.investor_registration_trend),
    [portfolioData.investor_registration_trend]
  );
  const hasInvestorRegistrationTrendData = useMemo(
    () => investorRegistrationTrendData.some((entry) => entry.registered > 0 || entry.invested > 0),
    [investorRegistrationTrendData]
  );
  const sellHistoryRows = useMemo(
    () =>
      (portfolioData.sell_history || []).map((item) => ({
        id: String(item.product_id),
        name: item.product_name,
        logoUrl: item.product_logo_url,
        totalInvestors: item.total_investors,
        amount: formatRevenue(item.total_investment_amount),
        ticker: getProductTicker(item.product_name),
      })),
    [portfolioData.sell_history]
  );
  const buyHistoryRows = useMemo(
    () =>
      (portfolioData.buy_history || []).map((item) => ({
        id: String(item.product_id),
        name: item.product_name,
        logoUrl: item.product_logo_url,
        qtySold: Number(item.quantity_sold) || 0,
        revenue: formatRevenue(item.revenue_earned),
        ticker: getProductTicker(item.product_name),
      })),
    [portfolioData.buy_history]
  );
  const documentsData = tabData[TAB_KEYS.documents] || {};
  const { uploaded: uploadedDocuments, pending: pendingDocuments } = splitPartnerDocuments(
    documentsData.uploaded_documents,
    documentsData.pending_documents,
  );
  const digioSignedDocument = documentsData.digio_signed_document;
  const hasDigioSignedDocument =
    digioSignedDocument &&
    typeof digioSignedDocument === 'object' &&
    (digioSignedDocument.document_url || digioSignedDocument.document_id);
  const investorsData = tabData[TAB_KEYS.investors] || {};
  const banksData = tabData[TAB_KEYS.banks] || [];
  const activeBankAccounts = (Array.isArray(banksData) ? banksData : []).filter(
    (bank) => bank.is_active !== false
  );
  const dematsData = tabData[TAB_KEYS.demats] || [];
  const communicationData = tabData[TAB_KEYS.communication] || {};
  const questionnaireData = tabData[TAB_KEYS.questionnaire] || [];

  const handleInvestorSellClick = useCallback(
    (record) => {
      const investorUuid = resolveInvestorUuid(record);
      if (!investorUuid) return;
      const params = new URLSearchParams({ investor_id: String(investorUuid).trim() });
      const partnerUuid = resolvePartnerUuid(record) || normalizedPartnerId;
      if (partnerUuid) params.set('partner_uuid', String(partnerUuid).trim());
      navigate(`${PLACE_SINGLE_SHARE_ORDER_PATH}?${params.toString()}`);
    },
    [navigate, normalizedPartnerId]
  );

  const investorsColumns = [
    { title: 'Investor Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Mobile', dataIndex: 'mobile', key: 'mobile' },
    { title: 'PAN', dataIndex: 'pan', key: 'pan' },
    {
      title: 'Created On',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => formatDate(value),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 88,
      render: (_, record) => (
        <PermissionGate permission={PERMISSIONS.INVESTORS_PLACE_SELL_ORDER}>
          <Button
            type="primary"
            size="small"
            disabled={!resolveInvestorUuid(record)}
            onClick={(e) => {
              e.stopPropagation();
              handleInvestorSellClick(record);
            }}
          >
            Sell
          </Button>
        </PermissionGate>
      ),
    },
  ];

  const [productTab, setProductTab] = useState('sell');

  return (
    <DashboardPageWrapper>
      <div className="space-y-0">
        {!normalizedPartnerId && (
          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message="Invalid partner identifier in URL. Please open profile from partner list."
          />
        )}
        {tabError.basic ? (
          <Alert type="error" showIcon message={tabError.basic} className="mb-4" />
        ) : null}

        <div className="rounded-t-2xl rounded-b-none shadow-sm border border-gray-100 overflow-hidden bg-white">
          <div className="bg-linear-to-r from-emerald-50/60 via-white to-white p-5 sm:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
              <div className="flex items-center gap-4 min-w-0">
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => navigate('/partners/list')}
                    className="mb-2 inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-800 transition-colors"
                  >
                    <FiArrowLeft size={14} />
                    Back to Partner List
                  </button>
                  <div className="flex items-center gap-2 min-w-0">
                    <h2 className="text-2xl font-bold text-gray-900 leading-tight truncate">{safeValue(basic.name)}</h2>
                    <PermissionGate permission={PERMISSIONS.PARTNER_BASIC_PROFILE_EDIT}>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => setBasicProfileDrawerOpen(true)}
                        aria-label="Edit basic profile"
                        className="shrink-0 text-gray-500 hover:text-gray-800"
                      >
                        Edit
                      </Button>
                    </PermissionGate>
                  </div>
                </div>
              </div>
              <div className="text-left lg:text-right">
                <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Created On</p>
                <p className="text-sm font-semibold text-gray-800 mt-1">{formatDate(basic.created_at)}</p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">PAN</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{safeValue(basic.pan)}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">Mobile</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{safeValue(basic.mobile)}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">Email</p>
                <p className="text-sm font-semibold text-gray-900 mt-1 break-all">{safeValue(basic.email)}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">Entity Type</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{safeValue(basic.entity_type)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-t-none rounded-b-2xl shadow-sm border border-gray-100 -mt-px bg-white overflow-hidden px-5 sm:px-6 pb-5 sm:pb-6">
          <Tabs
            activeKey={activeTab}
            onChange={(nextKey) => {
              setActiveTab(nextKey);
              ensureTabData(nextKey);
              const params = new URLSearchParams();
              if (nextKey !== TAB_KEYS.portfolio) params.set('tab', nextKey);
              setSearchParams(params, { replace: true });
            }}
            items={[
              {
                key: TAB_KEYS.portfolio,
                label: 'Portfolio',
                children: renderTabBody(
                  TAB_KEYS.portfolio,
                  <div className="flex flex-col gap-4 pt-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-3">Sell Stats</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        {SELL_STAT_CONFIG.map((item) => (
                          <div key={item.key} className="rounded-xl border border-gray-100 bg-white p-4">
                            <p className="text-xs text-gray-500 leading-snug">{item.label}</p>
                            <p className={`text-xl font-bold mt-2 ${item.color}`}>
                              {formatPortfolioStatValue(sellStats[item.key], item.type)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-3">Buy Stats</p>
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {BUY_STAT_CONFIG.map((item) => (
                          <div key={item.key} className="rounded-xl border border-gray-100 bg-white p-4">
                            <p className="text-xs text-gray-500 leading-snug">{item.label}</p>
                            <p className={`text-xl font-bold mt-2 ${item.color}`}>
                              {formatPortfolioStatValue(buyStats[item.key], item.type)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Card
                      className="rounded-xl border border-gray-100"
                      title={
                        <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                          <FiTrendingUp size={16} className="text-gray-500" />
                          Investment Trend
                        </span>
                      }
                    >
                      <div className="mb-3 flex items-center gap-2 bg-blue-50 rounded-lg px-4 py-2.5 w-fit">
                        <HiOutlineOfficeBuilding className="text-blue-500" size={16} />
                        <span className="text-sm font-semibold text-blue-600">
                          Total AUM: {formatRevenue(portfolioData.total_aum)}
                        </span>
                      </div>
                      {investmentTrendData.length === 0 ? (
                        <Empty description="No investment trend data available." className="py-10" />
                      ) : (
                        <ResponsiveContainer width="100%" height={240}>
                          <AreaChart data={investmentTrendData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                            <defs>
                              <linearGradient id="aumGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#93c5fd" stopOpacity={0.5} />
                                <stop offset="95%" stopColor="#93c5fd" stopOpacity={0.05} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                            <YAxis tickFormatter={formatAUM} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                            <Tooltip formatter={(value) => [formatRevenue(value), 'AUM']} />
                            <Legend formatter={() => 'AUM'} iconType="circle" />
                            <Area
                              type="monotone"
                              dataKey="aum"
                              stroke="#60a5fa"
                              strokeWidth={2}
                              fill="url(#aumGradient)"
                              dot={false}
                              activeDot={{ r: 5 }}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      )}
                    </Card>

                    <Card
                      className="rounded-xl border border-gray-100"
                      title={
                        <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                          <HiOutlineUserGroup size={16} className="text-gray-500" />
                          Investment vs Registration
                        </span>
                      }
                    >
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="rounded-lg bg-primary-muted border border-primary-light px-4 py-2.5 flex items-center gap-2">
                          <HiOutlineUserGroup className="text-primary" size={16} />
                          <span className="text-sm text-primary-active font-medium">
                            Registered Investors:{' '}
                            <strong>{formatIndianCount(investorRegistrationSummary.total_registered)}</strong>
                          </span>
                        </div>
                        <div className="rounded-lg bg-purple-50 border border-purple-100 px-4 py-2.5 flex items-center gap-2">
                          <FiTrendingUp className="text-purple-500" size={16} />
                          <span className="text-sm text-purple-700 font-medium">
                            Invested Investors:{' '}
                            <strong>{formatIndianCount(investorRegistrationSummary.total_invested)}</strong>
                          </span>
                        </div>
                      </div>
                      {!hasInvestorRegistrationTrendData ? (
                        <Empty description="No investor registration trend data available." className="py-10" />
                      ) : (
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart
                            data={investorRegistrationTrendData}
                            margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
                            barGap={4}
                            barCategoryGap="32%"
                            maxBarSize={12}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip />
                            <Legend iconType="square" />
                            <Bar dataKey="registered" name="Registered" fill={PRIMARY_COLOR} barSize={10} radius={[4, 4, 0, 0]} />
                            <Bar dataKey="invested" name="Invested" fill="#7c3aed" barSize={10} radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </Card>

                    <Card className="rounded-xl border border-gray-100">
                      <div className="flex gap-6 border-b border-gray-100 mb-4">
                        {['sell', 'buy'].map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setProductTab(t)}
                            className={`pb-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
                              productTab === t
                                ? 'text-green-600 border-green-600'
                                : 'text-gray-500 border-transparent hover:text-gray-700'
                            }`}
                          >
                            {t.charAt(0).toUpperCase() + t.slice(1)}
                          </button>
                        ))}
                      </div>

                      {productTab === 'sell' ? (
                        <Table
                          size="small"
                          className="[&_.ant-table-thead>tr>th]:whitespace-nowrap [&_.ant-table-tbody>tr>td]:whitespace-nowrap"
                          pagination={{ pageSize: 10, showSizeChanger: false }}
                          scroll={{ x: 'max-content' }}
                          rowKey="id"
                          dataSource={sellHistoryRows}
                          columns={[
                            {
                              title: '',
                              key: 'avatar',
                              width: 48,
                              render: (_, record) => (
                                <ProductAvatar logoUrl={record.logoUrl} ticker={record.ticker} name={record.name} />
                              ),
                            },
                            { title: 'Product ID', dataIndex: 'id', key: 'id', width: 110 },
                            { title: 'Product Name', dataIndex: 'name', key: 'name' },
                            {
                              title: 'Total Investors',
                              dataIndex: 'totalInvestors',
                              key: 'totalInvestors',
                              width: 130,
                              align: 'center',
                              render: (value) => formatIndianCount(value),
                            },
                            { title: 'Total Investment Amount', dataIndex: 'amount', key: 'amount', width: 180, align: 'right' },
                          ]}
                          locale={{ emptyText: <Empty description="No sell history found for this partner." /> }}
                        />
                      ) : (
                        <Table
                          size="small"
                          className="[&_.ant-table-thead>tr>th]:whitespace-nowrap [&_.ant-table-tbody>tr>td]:whitespace-nowrap"
                          pagination={{ pageSize: 10, showSizeChanger: false }}
                          scroll={{ x: 'max-content' }}
                          rowKey="id"
                          dataSource={buyHistoryRows}
                          columns={[
                            {
                              title: '',
                              key: 'avatar',
                              width: 48,
                              render: (_, record) => (
                                <ProductAvatar logoUrl={record.logoUrl} ticker={record.ticker} name={record.name} />
                              ),
                            },
                            { title: 'Product ID', dataIndex: 'id', key: 'id', width: 110 },
                            { title: 'Product Name', dataIndex: 'name', key: 'name' },
                            {
                              title: 'Quantity Sold',
                              dataIndex: 'qtySold',
                              key: 'qtySold',
                              width: 130,
                              align: 'center',
                              render: (value) => formatIndianCount(value),
                            },
                            { title: 'Revenue Earned', dataIndex: 'revenue', key: 'revenue', width: 150, align: 'right' },
                          ]}
                          locale={{ emptyText: <Empty description="No buy history found for this partner." /> }}
                        />
                      )}
                    </Card>
                  </div>
                ),
              },
              {
                key: TAB_KEYS.documents,
                label: 'Documents',
                children: renderTabBody(
                  TAB_KEYS.documents,
                  <div className="flex flex-col gap-8 pt-4">
                    <Card title="Uploaded Documents" className="rounded-xl border border-gray-100 [&_.ant-card-body]:pt-4">
                      {uploadedDocuments.length === 0 ? (
                        <Empty description="No uploaded documents found." />
                      ) : (
                        <div className="space-y-3">
                          {uploadedDocuments.map((doc) => {
                            const fileUrl = resolveFileUrl(doc.document_url);
                            const documentId = getKycDocumentId(doc);
                            const docStatus = String(doc.status || 'UPLOADED').toUpperCase();
                            const isReviewing = reviewingDocId && String(reviewingDocId) === String(documentId);
                            return (
                              <div key={`${documentId}-${doc.document_url}`} className="rounded-xl border border-gray-200 p-4">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-sm font-semibold text-gray-900">{doc.document_type}</p>
                                    <p className="text-xs text-gray-500 mt-1 break-all">{fileUrl}</p>
                                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                                      <Tag color={getUploadedDocTagColor(docStatus)}>{docStatus}</Tag>
                                      {fileUrl ? (
                                        <a href={fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                          View document
                                        </a>
                                      ) : null}
                                    </div>
                                    {canReviewKycDocuments && documentId ? (
                                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                                        <Button
                                          type="primary"
                                          size="small"
                                          loading={isReviewing}
                                          disabled={isReviewing || docStatus === 'VERIFIED'}
                                          onClick={() => setApproveDoc(doc)}
                                        >
                                          Approve
                                        </Button>
                                        <Button
                                          danger
                                          size="small"
                                          loading={isReviewing}
                                          disabled={isReviewing}
                                          onClick={() => {
                                            setRejectDoc(doc);
                                            setRejectionReason('');
                                          }}
                                        >
                                          Reject
                                        </Button>
                                      </div>
                                    ) : null}
                                  </div>
                                  {isImageUrl(fileUrl) ? (
                                    <img src={fileUrl} alt={doc.document_type} className="w-16 h-16 rounded-md object-cover border border-gray-200" />
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Card>

                    {hasDigioSignedDocument ? (
                      <Card title="Digio Signed Document" className="rounded-xl border border-gray-100 [&_.ant-card-body]:pt-4">
                        <div className="rounded-xl border border-gray-200 p-5">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900">
                                {digioSignedDocument.label || 'Digio Signed Document'}
                              </p>
                              {digioSignedDocument.document_id ? (
                                <p className="text-xs text-gray-500 mt-1">
                                  Document ID: {digioSignedDocument.document_id}
                                </p>
                              ) : null}
                              <div className="mt-2 flex items-center gap-2 flex-wrap">
                                <Tag color={digioSignedDocument.status === 'completed' ? 'green' : 'default'}>
                                  {(digioSignedDocument.status || 'UNKNOWN').toUpperCase()}
                                </Tag>
                                {digioSignedDocument.document_url ? (
                                  <a
                                    href={resolveFileUrl(digioSignedDocument.document_url)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    View document
                                  </a>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ) : null}

                    <Card title="Pending Documents Upload" className="rounded-xl border border-gray-100 [&_.ant-card-body]:pt-4">
                      {pendingDocuments.length === 0 ? (
                        <Empty description="No pending documents. All done." />
                      ) : (
                        <div className="space-y-3">
                          {pendingDocuments.map((doc) => {
                            const rejectedFileUrl = resolveFileUrl(doc.document_url);
                            const uploadLabel = doc.is_rejected ? 'Re-upload' : 'Upload File';
                            return (
                            <div key={`${doc.key}-${doc.document_type}`} className="rounded-xl border border-gray-200 p-4">
                              <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-gray-900">{doc.label}</p>
                                  <p className="text-xs text-gray-500 mt-1">{doc.sub_label || doc.document_type}</p>
                                  <p className="text-[11px] text-gray-400 mt-1">Type: {doc.document_type}</p>
                                  {doc.is_rejected ? (
                                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                                      <Tag color="red">REJECTED</Tag>
                                      {rejectedFileUrl ? (
                                        <a href={rejectedFileUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                          View document
                                        </a>
                                      ) : null}
                                    </div>
                                  ) : null}
                                </div>
                                <label className="inline-flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 cursor-pointer">
                                  <FiUpload />
                                  {uploadingDocType === doc.document_type ? 'Uploading...' : uploadLabel}
                                  <input
                                    type="file"
                                    className="hidden"
                                    disabled={uploadingDocType === doc.document_type}
                                    onChange={(event) => {
                                      const selectedFile = event.target.files?.[0];
                                      if (selectedFile) handleUploadDocument(doc, selectedFile);
                                      event.target.value = '';
                                    }}
                                  />
                                </label>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </Card>
                  </div>
                ),
              },
              {
                key: TAB_KEYS.investors,
                label: 'Associated Investors',
                children: renderTabBody(
                  TAB_KEYS.investors,
                  <Card className="rounded-xl">
                    <div className="overflow-x-auto">
                      <Table
                        columns={investorsColumns}
                        dataSource={investorsData.data || []}
                        rowKey={(record) => record.investor_uuid ?? record.investor_id}
                        scroll={{ x: 900 }}
                        loading={investorsFetching}
                        pagination={{
                          current: investorsData.pagination?.page || 1,
                          pageSize: investorsData.pagination?.limit || 10,
                          total: investorsData.pagination?.total || 0,
                          showSizeChanger: false,
                          showTotal: (count, range) => (
                            <span className="text-xs text-gray-500">
                              {range[0]}–{range[1]} of {count}
                            </span>
                          ),
                        }}
                        onChange={(nextPagination) => {
                          loadInvestors(nextPagination.current, nextPagination.pageSize);
                        }}
                        locale={{ emptyText: <Empty description="No investors mapped to this partner." /> }}
                      />
                    </div>
                  </Card>
                ),
              },
              {
                key: TAB_KEYS.banks,
                label: 'Bank Details',
                children: renderTabBody(
                  TAB_KEYS.banks,
                  <Card
                    className="rounded-xl"
                    title="Bank Accounts"
                    extra={
                      <PermissionGate permission={PERMISSIONS.PARTNER_EDIT}>
                        <Button
                          type={isBankFormOpen ? 'default' : 'primary'}
                          icon={isBankFormOpen ? <FiX /> : <FiPlus />}
                          onClick={() => {
                            if (isBankFormOpen) {
                              resetProfileBankAddForm({
                                bankForm,
                                setBankIfscDetails,
                                setBankIfscError,
                                setManualUploadData: setBankManualUploadData,
                                setIsBankFormOpen,
                              });
                            } else {
                              setIsBankFormOpen(true);
                            }
                          }}
                        >
                          {isBankFormOpen ? 'Cancel' : 'Add Bank'}
                        </Button>
                      </PermissionGate>
                    }
                  >
                    <div className="flex flex-col gap-6">
                      <BankMismatchReviewPanel
                        reviewData={bankMismatchReview}
                        loading={bankMismatchLoading}
                        error={bankMismatchError}
                        actionLoading={bankMismatchActionLoading}
                        canAct={canManageBankDematAccounts}
                        onVerify={handleBankMismatchVerify}
                      />

                      {canManageBankDematAccounts && isBankFormOpen ? (
                        <div className="rounded-xl border border-green-100 bg-green-50/30 p-4">
                          {bankManualUploadData ? (
                            <BankManualUploadForm
                              manualUploadData={bankManualUploadData}
                              loading={savingBank}
                              onSubmit={handleManualUploadSubmit}
                              onCancel={handleManualUploadCancel}
                            />
                          ) : (
                          <Form
                            form={bankForm}
                            layout="vertical"
                            onFinish={handleAddBankAccount}
                          >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                              <Form.Item
                                label="Account Number"
                                name="account_number"
                                rules={[{ required: true, message: 'Enter account number.' }]}
                              >
                                <Input placeholder="Enter bank account number" disabled={savingBank} />
                              </Form.Item>
                              <Form.Item
                                label="IFSC Code"
                                name="ifsc_code"
                                normalize={(value) => String(value || '').toUpperCase()}
                                rules={[
                                  { required: true, message: 'Enter IFSC code.' },
                                  { pattern: IFSC_REGEX, message: 'Enter a valid IFSC code.' },
                                ]}
                              >
                                <Input
                                  placeholder="HDFC0000522"
                                  maxLength={11}
                                  disabled={savingBank}
                                  onChange={(event) => handleBankIfscChange(event.target.value)}
                                />
                              </Form.Item>
                            </div>

                            {bankIfscLookupLoading ? (
                              <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                                Fetching bank details...
                              </div>
                            ) : null}
                            {bankIfscDetails ? (
                              <div className="mb-4 rounded-lg border border-green-100 bg-white px-4 py-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">IFSC Bank Details</p>
                                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                  <p><span className="text-gray-500">Bank:</span> {safeValue(bankIfscDetails.bank_name)}</p>
                                  <p><span className="text-gray-500">Branch:</span> {safeValue(bankIfscDetails.branch_name)}</p>
                                  {bankIfscDetails.address ? (
                                    <p className="md:col-span-2">
                                      <span className="text-gray-500">Address:</span> {bankIfscDetails.address}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                            ) : null}
                            {bankIfscError ? (
                              <Alert type="error" showIcon message={bankIfscError} className="mb-4" />
                            ) : null}

                            <div className="flex justify-end gap-2">
                              <Button
                                onClick={() => {
                                  resetProfileBankAddForm({
                                    bankForm,
                                    setBankIfscDetails,
                                    setBankIfscError,
                                    setManualUploadData: setBankManualUploadData,
                                    setIsBankFormOpen,
                                  });
                                }}
                                disabled={savingBank}
                              >
                                Cancel
                              </Button>
                              <Button type="primary" htmlType="submit" loading={savingBank}>
                                Add Bank Account
                              </Button>
                            </div>
                          </Form>
                          )}
                        </div>
                      ) : null}

                      {activeBankAccounts.length === 0 ? (
                        <Empty description="No bank details available." />
                      ) : (
                      <div className="space-y-3">
                        {activeBankAccounts.map((bank) => (
                          <div key={bank.bank_account_id || bank.id || bank.uuid} className="rounded-xl border border-gray-200 p-4 bg-gray-50/40">
                            <div className="flex items-center justify-between gap-3 mb-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <p className="text-sm font-semibold text-gray-900">{safeValue(bank.bank_name)}</p>
                                {bank.is_primary ? (
                                  <Tag color="green" className="m-0 font-medium">
                                    Primary
                                  </Tag>
                                ) : null}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {canSetPrimaryBank && !bank.is_primary && bank.is_verified && bank.uuid ? (
                                  <Popconfirm
                                    title="Set as primary bank account?"
                                    description="This will demote the current primary account."
                                    okText="Set Primary"
                                    onConfirm={() => handleSetPrimaryBank(bank.uuid)}
                                  >
                                    <Button
                                      size="small"
                                      loading={settingPrimaryBankUuid === bank.uuid}
                                      disabled={Boolean(
                                        (settingPrimaryBankUuid && settingPrimaryBankUuid !== bank.uuid) ||
                                          deletingBankUuid
                                      )}
                                    >
                                      Set as Primary
                                    </Button>
                                  </Popconfirm>
                                ) : null}
                                {canManageBankDematAccounts && bank.uuid ? (
                                  <Popconfirm
                                    title="Remove this bank account?"
                                    description="This will deactivate the bank account."
                                    okText="Remove"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => handleDeleteBankAccount(bank.uuid)}
                                  >
                                    <Button
                                      size="small"
                                      danger
                                      icon={<DeleteOutlined />}
                                      loading={deletingBankUuid === bank.uuid}
                                      disabled={Boolean(
                                        (deletingBankUuid && deletingBankUuid !== bank.uuid) ||
                                          settingPrimaryBankUuid
                                      )}
                                    >
                                      Delete
                                    </Button>
                                  </Popconfirm>
                                ) : null}
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                              <p><span className="text-gray-500">Account Number:</span> {safeValue(bank.account_number)}</p>
                              <p><span className="text-gray-500">IFSC:</span> {safeValue(bank.ifsc || bank.ifsc_code)}</p>
                              <p><span className="text-gray-500">Account Holder:</span> {safeValue(bank.account_holder_name || bank.beneficiary_name || bank.verified_name)}</p>
                              <p><span className="text-gray-500">Verified:</span> {bank.is_verified ? 'Yes' : 'No'}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      )}
                    </div>
                  </Card>
                ),
              },
              {
                key: TAB_KEYS.demats,
                label: 'Demat Details',
                children: renderTabBody(
                  TAB_KEYS.demats,
                  <Card
                    className="rounded-xl border border-gray-100 [&_.ant-card-body]:pt-4"
                    title="Demat Accounts"
                    extra={
                      <PermissionGate permission={PERMISSIONS.PARTNER_EDIT}>
                        <Button
                          type={isDematFormOpen ? 'default' : 'primary'}
                          icon={isDematFormOpen ? <FiX /> : <FiPlus />}
                          onClick={() => {
                            if (isDematFormOpen) {
                              dematForm.resetFields();
                              setDematCmrFile(null);
                            }
                            setIsDematFormOpen((open) => !open);
                          }}
                        >
                          {isDematFormOpen ? 'Cancel' : 'Add Demat'}
                        </Button>
                      </PermissionGate>
                    }
                  >
                    <div className="space-y-4">
                      {canManageBankDematAccounts && isDematFormOpen ? (
                        <div className="rounded-xl border border-green-100 bg-green-50/30 p-4">
                          <Form
                            form={dematForm}
                            layout="vertical"
                            onFinish={handleAddDematAccount}
                          >
                            <Form.Item
                              label="Demat Number"
                              name="demat_number"
                              normalize={formatDematInput}
                              validateTrigger={['onChange', 'onBlur']}
                              rules={[
                                { required: true, message: 'Enter demat number.' },
                                {
                                  validator: async (_, value) => {
                                    const inlineError = getDematInputError(value);
                                    if (inlineError) throw new Error(inlineError);
                                    const result = validateDematNumber(value);
                                    if (!result.valid) throw new Error(result.message);
                                  },
                                },
                              ]}
                              extra={<span className="text-xs text-gray-500">{DEMAT_FORMAT_HINT}</span>}
                            >
                              <Input
                                placeholder="IN12345678901234 or 16-digit CDSL"
                                maxLength={DEMAT_MAX_LENGTH}
                                disabled={savingDemat}
                              />
                            </Form.Item>

                            <div className="mb-4 rounded-lg border border-dashed border-gray-300 bg-white px-4 py-3">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-800">CMR Document</p>
                                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                                    {dematCmrFile ? dematCmrFile.name : 'Upload PDF, JPG, PNG, or WebP under 2 MB.'}
                                  </p>
                                </div>
                                <label className="inline-flex items-center justify-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 cursor-pointer hover:bg-green-100">
                                  <FiUpload />
                                  Choose File
                                  <input
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                                    disabled={savingDemat}
                                    onChange={(event) => {
                                      const selectedFile = event.target.files?.[0];
                                      handleDematCmrFileChange(selectedFile);
                                      event.target.value = '';
                                    }}
                                  />
                                </label>
                              </div>
                            </div>

                            <div className="flex justify-end gap-2">
                              <Button
                                onClick={() => {
                                  dematForm.resetFields();
                                  setDematCmrFile(null);
                                  setIsDematFormOpen(false);
                                }}
                                disabled={savingDemat}
                              >
                                Cancel
                              </Button>
                              <Button type="primary" htmlType="submit" loading={savingDemat}>
                                Add Demat Account
                              </Button>
                            </div>
                          </Form>
                        </div>
                      ) : null}

                      {(dematsData || []).length === 0 ? (
                        <Empty description="No demat details available." />
                      ) : (
                      <div className="space-y-4">
                        {(dematsData || []).map((demat) => (
                          <DematAccountCard
                            key={demat.demat_account_id || demat.id || demat.uuid}
                            demat={demat}
                            onEdit={
                              canEditDematNumber && demat.uuid
                                ? handleOpenDematEdit
                                : undefined
                            }
                          />
                        ))}
                      </div>
                      )}
                    </div>
                  </Card>
                ),
              },
              {
                key: TAB_KEYS.configuration,
                label: 'Configuration',
                children: (
                  <div className="space-y-4 pt-2">
                    <div>
                      <h3 className="text-2xl font-semibold text-gray-900">Configuration</h3>
                      <p className="text-base text-gray-500 mt-2">
                        Manage trusted partner flow and partner margin settings for this partner.
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">Allow share transfer before payment</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Trusted partner flow: shares transfer first, payment 1–2 days later, deal slip after payment.
                        </p>
                      </div>
                      <Switch
                        checked={Boolean(basic.allow_transfer_before_payment)}
                        disabled={!canEditPartner}
                        loading={savingTransferBeforePayment}
                        onChange={handleTransferBeforePaymentChange}
                      />
                    </div>

                    {canViewPartnerMargin ? (
                      <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-gray-900">Partner margin cap</p>
                              <Tag color="blue" className="m-0">
                                Effective: {effectiveMarginCap}%
                              </Tag>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Default max margin is {DEFAULT_PARTNER_MARGIN_CAP}%. Enable override for trusted or selected
                              partners and set a fixed upper limit ({DEFAULT_PARTNER_MARGIN_CAP + 1}–{ADMIN_MAX_PARTNER_MARGIN_CAP}%).
                            </p>
                          </div>
                          <Switch
                            checked={Boolean(marginCapDraft.is_margin_cap_override_enabled)}
                            disabled={!canEditPartnerMargin}
                            loading={savingMarginCap}
                            onChange={handleMarginOverrideChange}
                          />
                        </div>

                        {marginCapDraft.is_margin_cap_override_enabled ? (
                          <div className="mt-3 pt-3 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-gray-700 mb-1.5">Max margin above partner cost</p>
                              <InputNumber
                                min={DEFAULT_PARTNER_MARGIN_CAP + 0.01}
                                max={ADMIN_MAX_PARTNER_MARGIN_CAP}
                                precision={2}
                                step={0.5}
                                addonAfter="%"
                                value={marginCapDraft.max_margin_cap_percentage}
                                disabled={!canEditPartnerMargin || savingMarginCap}
                                onChange={(value) => {
                                  setMarginCapDraft((prev) => ({
                                    ...prev,
                                    max_margin_cap_percentage: value,
                                  }));
                                }}
                                className="w-full sm:w-36"
                              />
                            </div>
                            {canEditPartnerMargin && hasMarginCapChanges ? (
                              <Button
                                type="primary"
                                size="small"
                                loading={savingMarginCap}
                                onClick={handleSaveMarginCap}
                                className="sm:self-end"
                              >
                                Save margin cap
                              </Button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ),
              },
              {
                key: TAB_KEYS.communication,
                label: 'Communication',
                children: renderTabBody(
                  TAB_KEYS.communication,
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-2xl font-semibold text-gray-900">Communication Settings</h3>
                      <p className="text-base text-gray-500 mt-2">
                        Configure how you want to receive and share payment notifications with your investors.
                      </p>
                    </div>
                    <div className="flex items-center justify-end">
                      {!isCommunicationEditing ? (
                        <Button onClick={() => setIsCommunicationEditing(true)}>Edit Settings</Button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Button onClick={() => { setCommunicationDraft(communicationData); setIsCommunicationEditing(false); }}>
                            Cancel
                          </Button>
                          <Button type="primary" loading={savingCommunication} onClick={handleSaveCommunication}>
                            Save
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {COMMUNICATION_FIELDS.map((field) => (
                        <div key={field.key} className={`rounded-xl border p-4 ${field.cardClass}`}>
                          <div className="flex items-start justify-between gap-3 mb-5">
                            <div className={`w-11 h-11 rounded-xl border flex items-center justify-center shadow-[0_2px_6px_rgba(0,0,0,0.04)] ${field.iconClass}`}>
                              <field.Icon size={20} />
                            </div>
                            <Switch
                              checked={Boolean(communicationDraft[field.key])}
                              disabled={!isCommunicationEditing}
                              onChange={(checked) => {
                                setCommunicationDraft((prev) => ({ ...prev, [field.key]: checked }));
                              }}
                            />
                          </div>
                          <p className={`text-base leading-6 font-semibold ${field.titleClass}`}>{field.title}</p>
                          <p className="text-base leading-6 text-gray-600 mt-1">{field.subtitle}</p>
                          <div className="mt-4 pt-3 border-t border-black/5 flex items-center gap-2">
                            <field.Icon size={14} className={field.footerClass} />
                            <span className={`text-base leading-6 font-medium ${field.footerClass}`}>{field.footer}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ),
              },
              {
                key: TAB_KEYS.performanceReport,
                label: 'Performance Report',
                children: (
                  <PartnerPerformanceReportTab
                    partnerId={normalizedPartnerId}
                    partnerName={basic.name}
                  />
                ),
              },
              {
                key: TAB_KEYS.questionnaire,
                label: 'Questionnaire',
                children: renderTabBody(
                  TAB_KEYS.questionnaire,
                  <PartnerQuestionnaireTab items={questionnaireData} />,
                ),
              },
            ]}
          />
        </div>
      </div>

      <Modal
        title="Edit Demat Number"
        open={Boolean(editingDemat)}
        onCancel={handleCloseDematEdit}
        footer={null}
        destroyOnClose
      >
        <Form form={dematEditForm} layout="vertical" onFinish={handleEditDematNumber}>
          <Form.Item
            label="Demat Number"
            name="demat_number"
            normalize={formatDematInput}
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              { required: true, message: 'Enter demat number.' },
              {
                validator: async (_, value) => {
                  const inlineError = getDematInputError(value);
                  if (inlineError) throw new Error(inlineError);
                  const result = validateDematNumber(value);
                  if (!result.valid) throw new Error(result.message);
                },
              },
            ]}
            extra={<span className="text-xs text-gray-500">{DEMAT_FORMAT_HINT}</span>}
          >
            <Input
              placeholder="IN12345678901234 or 16-digit CDSL"
              maxLength={DEMAT_MAX_LENGTH}
              disabled={savingDematEdit}
            />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={handleCloseDematEdit} disabled={savingDematEdit}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={savingDematEdit}>
              Save
            </Button>
          </div>
        </Form>
      </Modal>

      <BasicProfileEditDrawer
        open={basicProfileDrawerOpen}
        title={`Edit Basic Profile${basic.name ? ` — ${basic.name}` : ''}`}
        initialValues={{
          mobile: basic.mobile || '',
          email: basic.email || '',
          dob: basic.dob || '',
        }}
        submitting={savingBasicProfile}
        onClose={() => setBasicProfileDrawerOpen(false)}
        onSubmit={handleBasicProfileSubmit}
      />

      <Modal
        title={`Approve ${approveDoc?.document_type || 'document'}`}
        open={Boolean(approveDoc)}
        onCancel={() => {
          if (reviewingDocId) return;
          setApproveDoc(null);
        }}
        okText="Approve"
        okButtonProps={{ type: 'primary', loading: Boolean(reviewingDocId) }}
        onOk={() => handleReviewDocument(approveDoc, 'VERIFIED')}
        destroyOnClose
      >
        <p className="text-sm text-gray-700 mb-2">
          Are you sure you want to approve this document?
        </p>
        <p className="text-sm text-gray-600">
          After approval you cannot upload this document again unless you reject it later. Please view the file once before confirming.
        </p>
      </Modal>

      <Modal
        title={`Reject ${rejectDoc?.document_type || 'document'}`}
        open={Boolean(rejectDoc)}
        onCancel={() => {
          if (reviewingDocId) return;
          setRejectDoc(null);
          setRejectionReason('');
        }}
        okText="Reject"
        okButtonProps={{ danger: true, loading: Boolean(reviewingDocId) }}
        onOk={() => handleReviewDocument(rejectDoc, 'REJECTED', rejectionReason.trim())}
        destroyOnClose
      >
        <p className="text-sm text-gray-600 mb-3">
          Partner will get a document rejected email. Reason is optional and is shown in that email.
        </p>
        <Input.TextArea
          rows={3}
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          placeholder="Blurry address proof"
        />
      </Modal>

      <BankRequestSubmittedModal
        open={bankSubmitSuccessOpen}
        message={bankSubmitSuccessMessage}
        onClose={() => {
          setBankSubmitSuccessOpen(false);
          setBankSubmitSuccessMessage('');
        }}
      />
    </DashboardPageWrapper>
  );
}
